<template>
  <section class="multi-factor-auth-form">
    <ValidationObserver
      tag="div"
      class="form-container"
      v-slot="{ handleSubmit, pristine, invalid }"
    >
      <h1
        class="title"
        v-t="'appMultiFactorAuthForm.message.multiFactorAuthentication'"
      />
      <p class="sub-title" v-t="'appMultiFactorAuthForm.message.description'" />
      <div class="multi-factor-auth-error">
        <p v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </p>
      </div>
      <b-form v-on:submit.prevent="handleSubmit($_onSubmit)">
        <ValidationProvider
          v-bind:key="form.key"
          tag="div"
          v-bind:name="form.id.form"
          v-bind:rules="form.rules"
          class="form-item"
          v-slot="validationContext"
        >
          <b-form-input
            v-bind:id="form.id.form"
            v-bind:type="form.type"
            v-model="value[form.key]"
            v-bind:state="$_toState(validationContext)"
            v-bind:placeholder="form.placeholder"
            v-bind:tabindex="form.tabindex"
            v-bind:autocomplete="form.autocomplete"
            class="form"
            v-bind:formatter="$_formatOtp"
          />
          <div class="form-related">
            <button
              class="button-clear"
              v-bind:class="{ visible: form.clearable }"
              v-on:mousedown.prevent
              v-on:click="value[form.key] = null"
              type="button"
            >
              <icon-clear class="icon-clear" />
            </button>
            <div class="icon-validation" />
          </div>

          <b-form-invalid-feedback v-bind:id="form.id.feedback">
            <p class="invalid-message">
              {{ form.invalidMessage }}
            </p>
          </b-form-invalid-feedback>
        </ValidationProvider>
        <b-button
          type="submit"
          variant="success"
          class="btn-proceed"
          tabindex="2"
          v-bind:disabled="pristine || invalid"
          v-t="'appMultiFactorAuthForm.action.login'"
        />
        <div class="cancel-button">
          <a
            tabindex="3"
            v-on:click="$_onClickCancel"
            v-on:keyup.enter="$_onClickCancel"
            v-t="'appMultiFactorAuthForm.message.cancel'"
          />
        </div>
      </b-form>
    </ValidationObserver>
  </section>
</template>

<script>
import IconClear from '@/assets/ifield/text-clear.svg?component'

/**
 * フォームキー定数
 */
const FORM_KEY = {
  OTP: 'otp',
}

export default {
  name: 'AppMultiFactorAuthForm',
  components: {
    'icon-clear': IconClear,
  },
  props: {
    /**
     * ワンタイムパスワード
     */
    otp: {
      type: String,
      default: null,
    },
    /**
     * エラーメッセージ
     */
    errorMessage: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      value: {
        otp: null,
      },
      rules: {
        otp: { required: true, min: 6, max: 6 },
      },
      placeholder: {
        otp: this.$t('appMultiFactorAuthForm.otp'),
      },
      invalidMessage: {
        otp: this.$t('appMultiFactorAuthForm.message.invalid.otp'),
      },
    }
  },
  computed: {
    /**
     * フォームのキー
     * @type {Object}
     */
    FORM_KEY: function () {
      return FORM_KEY
    },
    /**
     * フォーム値が空の場合はtrue
     * @param {string} フォーム値
     * @returns {boolean}
     */
    isBlank: function () {
      return this.value[FORM_KEY.OTP] == null || this.value[FORM_KEY.OTP] === ''
    },
    /**
     * フォーム定義
     * @type {Array.<Object>}
     */
    form: function () {
      return {
        key: FORM_KEY.OTP,
        id: {
          form: `input-${FORM_KEY.OTP}`,
          feedback: `input-${FORM_KEY.OTP}-feedback`,
        },
        rules: this.rules[FORM_KEY.OTP],
        placeholder: this.placeholder[FORM_KEY.OTP],
        invalidMessage: this.invalidMessage[FORM_KEY.OTP],
        tabindex: 1,
        clearable: !this.isBlank,
        autocomplete: 'one-time-code',
      }
    },
  },
  methods: {
    /**
     * 入力値を半角数字のみにフォーマットする
     * @param {string} value 入力値
     * @returns {string} フォーマット後の値
     */
    $_formatOtp(value) {
      return value ? value.replace(/[^0-9]/g, '') : value
    },
    /**
     * バリデーションフラグを状態に変換
     * @param {Object} validationContext vee-validateのコンテキスト
     * @param {boolean} validationContext.dirty ダーティーフラグ
     * @param {boolean} validationContext.changed 変更フラグ
     * @param {boolean} validationContext.valid validフラグ
     * @param {Object} validationContext.failedRules 失敗したルール
     * @returns {boolean} 状態
     */
    $_toState: function ({ dirty, changed, valid, failedRules }) {
      if (!dirty && !changed) return null
      // minルールでエラーになっている場合は、入力中とみなしてstateをnullにする
      return failedRules.min ? null : valid
    },

    /**
     * 続行ボタン押下時処理
     */
    $_onSubmit: function () {
      /**
       * ログインイベント
       * @param {Object} value ログイン情報(loginCode, password)
       */
      this.$emit('login', this.value)
    },

    /**
     * キャンセルリンクリック時処理
     */
    $_onClickCancel: function () {
      /**
       * キャンセルイベント
       */
      this.$emit('cancel')
    },
  },
}
</script>

<style lang="scss" scoped>
.multi-factor-auth-form {
  width: 27.5rem;
  background-color: #ffffff;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.05);
  border: 1px solid #aaaaaa;
  padding: 48px;
}

.title {
  font-size: $font-size-xlarge;
  text-align: center;
  font-weight: $font-weight-bold;
  line-height: 1;
  margin: 0 0 8px 0;
}
.sub-title {
  font-size: $font-size-middle;
  text-align: left;
  // font-weight: $font-weight-bold;
  line-height: 1.5;
  margin: 2rem 0 0 0;
}

.multi-factor-auth-error {
  margin: 0;
  min-height: 2rem;
  height: auto;

  .error-message {
    margin: 0;
    text-align: center;
    color: #dc3545;
    font-size: $font-size-middle;
    line-height: 1.25;
    word-break: break-all;
    overflow-wrap: break-word;
  }
}

// ユーザの入力する部分に関する設定
.form-item {
  position: relative;
  margin: 0;
  height: 5rem;

  .form {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    height: 2.5rem;
    padding-right: 68px !important;
    border: 1px solid #aaaaaa;
    border-radius: 4px;
    background-image: none !important;
  }
}

// クリアボタンとバリデーションアイコンを表示する部分の設定
// オートコンプリート時におけるバリデーションアイコン非表示対策で設定
.form-related {
  position: absolute;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: auto;
  height: 2.5rem;
  right: 0.5rem;
  top: 0;

  .form:focus-within + & {
    .button-clear.visible {
      display: block;
    }
  }
}

// クリアボタンに関する設定
.button-clear {
  appearance: none;
  outline: none;
  border: none;
  background: none;

  display: none;
  width: 1.625rem;
  height: 1.625rem;
  padding: 0;
  top: 0;

  z-index: 40;

  &:hover {
    background: #eee;
    border-radius: 50%;
  }

  .icon-clear {
    display: block;
    position: relative;
    margin: auto;
    fill: $entity-color-black;
  }
}

// バリデーションアイコンに関する設定
.icon-validation {
  display: block;
  top: 0;
  bottom: 0;
  right: 0;
  width: 0;
  height: 2.5rem;
  background-origin: padding-box;
  background-image: none !important;
  background-repeat: no-repeat;
  background-size: 1.125rem;
  background-position: center;

  z-index: 20;

  // バリデーションが有効になった場合に、独自でバリデーションアイコンを設定する。
  .is-valid + .form-related > & {
    width: 1.5rem;
    height: 100%;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e") !important;
  }

  .is-invalid + .form-related > & {
    width: 1.5rem;
    height: 100%;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e") !important;
  }
}

.invalid-message {
  text-align: left;
  margin: 0;
}

// 続行ボタンに関する設定
.btn-proceed {
  width: 100%;
  height: 2.5rem;
  color: #fff;
  font-weight: $font-weight-bold;
  background-color: #1ebe67;
  border-color: #1ebe67;
  margin-bottom: 24px;

  &:hover,
  &:focus {
    color: #fff;
    background-color: #12a554;
    border-color: #1ebe67;
  }

  // 入力フォームに何も無いときは、ボタンをhoverしても色を灰色にする
  &.disabled {
    pointer-events: none;
    background-color: #6c757d;
    border-color: #6c757d;
  }
}

// キャンセルボタンに関する設定
.cancel-button {
  color: #1ca570;
  font-size: $font-size-middle;
  display: flex;
  justify-content: center;
  cursor: pointer;
  line-height: 1;

  &:hover {
    text-decoration: underline;
  }
}
</style>
